import { useEffect, useState } from 'react';
import { SectionTitle, StatusPill } from '../UI';
import { api } from '../../api';

const fmtDate = (value) => value ? new Date(value).toLocaleString() : '—';
const fmtMoney = (value) => value ? `$${value.toFixed(4)}` : '—';

export default function CRMTab({ projects, selectedProject, onSelectProject }) {
  const [details, setDetails] = useState(selectedProject);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    async function loadDetails() {
      if (!selectedProject?.id) {
        setDetails(null);
        return;
      }
      setLoading(true);
      try {
        const project = await api.getProject(selectedProject.id);
        setDetails(project);
      } finally {
        setLoading(false);
      }
    }
    loadDetails();
  }, [selectedProject?.id]);

  async function queueCombined(command) {
    if (!selectedProject?.id) {
      return;
    }
    const label = command === '/geo project-report-pdf' ? 'combined PDF' : 'combined report';
    try {
      await api.createJob({
        project_id: selectedProject.id,
        command,
        url: selectedProject.homepage_url || selectedProject.domain,
        model: details?.latest_model || 'gpt-5.2-codex',
      });
      setNotice(`Queued ${label} job.`);
    } catch (error) {
      setNotice(error.message || `Failed to queue ${label}.`);
    }
  }

  async function queueRunPdf(run) {
    if (!selectedProject?.id) {
      return;
    }
    try {
      await api.createJob({
        project_id: selectedProject.id,
        source_run_id: run.id,
        command: '/geo report-pdf',
        url: selectedProject.homepage_url || selectedProject.domain,
        model: run.model || details?.latest_model || 'gpt-5.2-codex',
      });
      setNotice(`Queued PDF job for ${run.command}.`);
    } catch (error) {
      setNotice(error.message || 'Failed to queue PDF job.');
    }
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: '700', marginBottom: '6px' }}>
          Projects
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--text-3)' }}>
          Every domain becomes a persistent project. New runs are appended to history and the latest snapshot is updated automatically.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: '18px' }}>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
            <SectionTitle>Saved projects</SectionTitle>
          </div>
          {projects.length === 0 ? (
            <div style={{ padding: '18px', color: 'var(--text-3)' }}>No saved projects yet. Log your first run from the Commands tab.</div>
          ) : (
            projects.map((project) => {
              const active = project.id === selectedProject?.id;
              return (
                <button
                  key={project.id}
                  onClick={() => onSelectProject(project.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: active ? 'rgba(0,229,160,0.06)' : 'transparent',
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    padding: '16px 18px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--text-1)' }}>{project.name}</div>
                    <StatusPill status={project.status} />
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-3)', marginBottom: '8px' }}>{project.domain}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-2)' }}>
                    <span>Latest score: {project.latest_geo_score ?? '—'}</span>
                    <span>{fmtDate(project.last_run_at)}</span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
          {!details && <div style={{ color: 'var(--text-3)' }}>Select a project to view its run history.</div>}
          {loading && <div style={{ color: 'var(--text-3)' }}>Loading project history...</div>}
          {details && !loading && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: '700' }}>{details.name}</div>
                  <div style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>{details.homepage_url || details.domain}</div>
                </div>
                <div style={{ display: 'grid', gap: '6px', textAlign: 'right' }}>
                  <StatusPill status={details.status} />
                  <div style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>Latest score: {details.latest_geo_score ?? '—'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <button onClick={() => queueCombined('/geo project-report')} style={buttonStyle}>Generate Combined Report</button>
                <button onClick={() => queueCombined('/geo project-report-pdf')} style={buttonStyle}>Generate Combined PDF</button>
              </div>
              {notice && <div style={{ color: 'var(--good)', fontFamily: 'var(--font-mono)', fontSize: '12px', marginBottom: '12px' }}>{notice}</div>}

              <SectionTitle>Run history</SectionTitle>
              <div style={{ display: 'grid', gap: '10px' }}>
                {(details.runs || []).length === 0 && <div style={{ color: 'var(--text-3)' }}>No runs have been logged for this project yet.</div>}
                {(details.runs || []).map((run) => (
                  <div key={run.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontSize: '13px' }}>{run.command}</div>
                      <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', fontSize: '12px' }}>{fmtDate(run.finished_at || run.started_at)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--text-2)', marginBottom: '8px' }}>
                      <span>Model: {run.model || '—'}</span>
                      <span>Status: {run.status}</span>
                      <span>Score: {run.geo_score ?? '—'}</span>
                      <span>Cost: {fmtMoney(run.estimated_cost)}</span>
                      <span>Tokens: {(run.input_tokens || 0) + (run.output_tokens || 0)}</span>
                    </div>
                    {run.summary && <div style={{ color: 'var(--text-3)', fontSize: '13px', lineHeight: 1.5 }}>{run.summary}</div>}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                      {run.snapshot?.report_path && run.job_id && <a href={`/api/jobs/${run.job_id}/artifact/report`} style={linkStyle}>Report</a>}
                      {run.snapshot?.pdf_path && run.job_id && <a href={`/api/jobs/${run.job_id}/artifact/pdf`} style={linkStyle}>PDF</a>}
                      {run.snapshot?.structured_report_path && run.job_id && <a href={`/api/jobs/${run.job_id}/artifact/data`} style={linkStyle}>Data</a>}
                      {(run.snapshot?.structured_report || run.snapshot?.structured_report_path) && !run.snapshot?.pdf_path && (
                        <button onClick={() => queueRunPdf(run)} style={linkButtonStyle}>Prepare PDF</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const linkStyle = {
  color: 'var(--accent)',
  textDecoration: 'none',
  fontSize: '12px',
  fontFamily: 'var(--font-mono)',
};

const buttonStyle = {
  padding: '10px 14px',
  background: 'var(--accent)',
  border: 'none',
  borderRadius: '8px',
  color: '#0a0a0f',
  fontSize: '12px',
  fontWeight: '700',
  fontFamily: 'var(--font-display)',
  cursor: 'pointer',
};

const linkButtonStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: 'var(--accent)',
  textDecoration: 'none',
  fontSize: '12px',
  fontFamily: 'var(--font-mono)',
  cursor: 'pointer',
};
